import type { JSX } from 'react';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';

import type { JiraIssue } from '~/services/api/jira';

import { CloneToProjectButton } from './CloneToProjectButton';
import { JiraIcon } from './JiraIcon';
import { getIssueTypeColor, getPriorityColor, getStatusColor } from './utils';

interface JiraIssueDetailProps {
  issue: JiraIssue | null;
}

function EmptyState(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8">
      <JiraIcon className="w-16 h-16 text-muted mb-4 opacity-30" />
      <h2 className="text-xl font-semibold text-foreground mb-2">Select a ticket</h2>
      <p className="text-muted max-w-md">
        Choose a Jira ticket from the list to view its details, clone it to a project, or open it in
        Jira.
      </p>
    </div>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function JiraIssueDetail({ issue }: JiraIssueDetailProps): JSX.Element {
  const handleOpenInJira = useCallback(() => {
    if (issue?.url) {
      window.open(issue.url, '_blank', 'noopener,noreferrer');
    }
  }, [issue?.url]);

  if (!issue) {
    return <EmptyState />;
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-6">
      {/* Summary/Title first */}
      <h1 className="text-2xl font-bold text-foreground mb-4">{issue.summary}</h1>

      {/* Issue key, status, priority second */}
      <div className="flex items-center gap-3 flex-wrap mb-6">
        <a
          href={issue.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary font-mono text-base font-semibold hover:underline"
        >
          {issue.key}
        </a>
        <span
          className={`px-3 py-1 text-sm font-medium rounded-full ${getIssueTypeColor(issue.issue_type)}`}
        >
          {issue.issue_type}
        </span>
        <span
          className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(issue.status)}`}
        >
          {issue.status}
        </span>
        <span
          className={`w-4 h-4 rounded-full ${getPriorityColor(issue.priority)}`}
          title={`Priority: ${issue.priority}`}
          aria-label={`Priority: ${issue.priority}`}
        />
      </div>

      {/* Labels */}
      {issue.labels.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-muted mb-2">Labels</h3>
          <div className="flex flex-wrap gap-2">
            {issue.labels.map(label => (
              <span
                key={label}
                className="px-3 py-1 text-sm font-medium bg-surface-hover text-foreground rounded-md"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-muted mb-2">Description</h3>
        {issue.description ? (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <p className="text-foreground whitespace-pre-wrap">{issue.description}</p>
          </div>
        ) : (
          <p className="text-muted italic">No description provided</p>
        )}
      </div>

      {/* Comments */}
      {issue.comments?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-muted mb-3">
            Comments ({issue.comments.length})
          </h3>
          <div className="space-y-4">
            {issue.comments.map((comment, index) => (
              <div
                key={`${comment.created}-${index}`}
                className="bg-surface-hover rounded-lg p-4 border border-border"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground text-sm">{comment.author}</span>
                  <span className="text-xs text-muted">{formatDate(comment.created)}</span>
                </div>
                <p className="text-foreground text-sm whitespace-pre-wrap">{comment.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="text-sm text-muted mb-8 pt-4 border-t border-border">
        Last updated: {formatDate(issue.updated)}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 pt-4 border-t border-border">
        {issue.linked_project_id ? (
          <Button
            as={Link}
            to={`/projects/${issue.linked_project_id}`}
            color="primary"
            endContent={<DocumentDuplicateIcon className="w-4 h-4" />}
          >
            Go to Project
          </Button>
        ) : (
          <CloneToProjectButton issue={issue} />
        )}
        <Button
          color="default"
          variant="bordered"
          onPress={handleOpenInJira}
          endContent={<ArrowTopRightOnSquareIcon className="w-4 h-4" />}
        >
          Open in Jira
        </Button>
      </div>
    </div>
  );
}
